import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'
import { App } from 'antd'
import { Formik, FormikHelpers } from 'formik'
import {
  initialPlayerValues,
  mapPlayerToForm,
  submitPlayer,
  toUpdatePayload,
  BE_TO_FIELD_TITLE,
  toSavedFormBaseline,
} from '@sportninja/common/features/profile/player'
import { formatPlayerName } from '@sportninja/common/features/profile/player/playerDetails/utils/formatPlayerName'
import {
  mapProfileExtrasToForm,
  toProfileExtrasPayload,
} from '@sportninja/common/features/profile/player/playerDetails/utils/playerProfileExtras'
import {
  ADMIN_BE_TO_FIELD_TITLE,
  ADMIN_BE_TO_FORM_FIELD,
  initialPlayerDetailsAdminValues,
} from '@sportninja/common/features/profile/player/playerDetails/constants'
import type { PlayerDetailsFormValues } from '@sportninja/common/features/profile/player/playerDetails/types'
import { PLAYER_SUCCESS_MESSAGES } from '@sportninja/common/features/team/player'
import {
  useMePlayerProfile,
  useUpdateMePlayerProfile,
  useMePlayerImageMutation,
} from './hooks/useMePlayerProfile'
import { scrollToFirstFieldError } from 'src/helpers/scrollToFirstFieldError'
import { formatSuccessToast } from 'src/helpers/formatSuccessToast'
import {
  SectionEditContext,
  type SectionEditContextValue,
} from './context/SectionEditContext'

export interface EditPlayerContextValue {
  /** The cold fetch or a save in flight. */
  isLoading: boolean
  /**
   * Cold fetch only — the form shows its skeleton. Optional: the roster flow
   * supplies its own context value and never reads it.
   */
  isFetchLoading?: boolean
  /** "First Last" of the fetched player, or '' until it loads. */
  playerName?: string
  onCancel: () => void
  /** The player being edited (undefined in a future "Add Player" flow). */
  playerId?: string
  /**
   * Canlan Salesforce ID straight off the profile response — display-only, so
   * it never enters the form. Optional: absent on other tenants, on players
   * without one, and in the roster flow (which supplies its own context value).
   */
  salesforceId?: string | null
}

// Exported so other features (e.g. the roster Add Player modal) can reuse the
// profile section components, which read `useEditPlayerContext`, by supplying
// their own compatible context value. The profile provider/behavior is unchanged.
export const EditPlayerContext = createContext<EditPlayerContextValue | null>(
  null
)

export const useEditPlayerContext = (): EditPlayerContextValue => {
  const ctx = useContext(EditPlayerContext)
  if (!ctx) {
    throw new Error(
      'useEditPlayerContext must be used within an EditPlayerProvider'
    )
  }
  return ctx
}

interface EditPlayerProviderProps {
  onClose: () => void
  onSuccess?: () => void
  /**
   * When provided, the player is fetched (GET /players/:id via React Query) and
   * the form is pre-populated. When absent, the form opens empty and no fetch
   * happens (supports a future "Add Player" entry).
   */
  playerId?: string
  children: React.ReactNode
}

export const EditPlayerProvider: React.FC<EditPlayerProviderProps> = ({
  onClose,
  onSuccess,
  playerId,
  children,
}) => {
  const { message } = App.useApp()
  const { data, loading, error } = useMePlayerProfile(playerId)
  // Read mode first (Player Details' pattern): one section is edited at a time.
  const [editingSection, setEditingSection] = useState<string | null>(null)
  const updatePlayer = useUpdateMePlayerProfile()
  const {
    uploadImage,
    deleteImage,
    isPending: isImagePending,
  } = useMePlayerImageMutation()

  // Keep the latest onClose in a ref so the error effect doesn't re-fire (and
  // double-toast / double-close) if a parent passes a new onClose identity.
  const onCloseRef = useRef(onClose)
  onCloseRef.current = onClose

  // Fetch failure → surface a toast and close the modal (exactly once per error).
  useEffect(() => {
    if (error) {
      message.error('Could not load player. Please try again.')
      onCloseRef.current()
    }
  }, [error, message])

  if (error) {
    return null
  }

  // Skeleton shows only during the cold initial fetch (no data yet). A later
  // background refetch (loading true while data is present) must NOT replace the
  // filled form with the skeleton — mirrors the formikKey guard below.
  const isFetchLoading = Boolean(playerId) && loading && !data

  // Formik key: mount as 'loading' only on the initial cold fetch. Once data has
  // arrived the key stays 'ready' for the modal's life — a later background
  // refetch must NOT remount Formik and discard in-progress edits (a refetch of
  // the saved values re-seeds it through `enableReinitialize` instead).
  const formikKey = Boolean(playerId) && loading && !data ? 'loading' : 'ready'

  // Edit Player fields plus the extra Player Details sections the form shows
  // (PROFILE_EXTRA_SECTIONS), as a player sees them on their own profile —
  // without the admin-only fields.
  const initialValues: PlayerDetailsFormValues = {
    ...(data ? mapPlayerToForm(data) : initialPlayerValues),
    ...initialPlayerDetailsAdminValues,
    ...(data ? mapProfileExtrasToForm(data, { audience: 'self' }) : {}),
  }

  const handleSubmit = async (
    values: PlayerDetailsFormValues,
    helpers: FormikHelpers<PlayerDetailsFormValues>
  ) => {
    if (!playerId) {
      // No target to save against (e.g. future Add Player path) — just close.
      helpers.setSubmitting(false)
      onSuccess?.()
      onClose()
      return
    }

    // Early-return guard: if nothing changed (no payload diff AND no image
    // change), leave edit mode silently without calling submitPlayer or showing
    // a toast. Only the edited section can differ, so the diff is its changes.
    const payload = {
      ...toUpdatePayload(values, initialValues),
      ...toProfileExtrasPayload(values, initialValues, { audience: 'self' }),
    }
    const imageChanged = Boolean(values.imageData) || values.imageDeleted
    if (Object.keys(payload).length === 0 && !imageChanged) {
      helpers.setSubmitting(false)
      setEditingSection(null)
      return
    }

    // Commit the staged image into the form so the next section's save never
    // re-sends it before the refetch (which re-seeds the form through
    // `enableReinitialize`) lands. setValues, not resetForm: a reset baseline
    // would differ from `initialValues` and be reinitialised back to stale data.
    const finishSection = () => {
      helpers.setValues(toSavedFormBaseline(values), false)
      setEditingSection(null)
    }

    try {
      // 1. Image step (only if changed). Runs BEFORE the PUT so a failure aborts
      //    the whole save without a partial silent write. The image mutation
      //    invalidates playerProfile.byId + userFamily.me itself.
      if (values.imageData) {
        await uploadImage({ playerId, file: values.imageData })
      } else if (values.imageDeleted) {
        await deleteImage({ playerId })
      }

      // 2. Image-only save (image changed but no field diff): submitPlayer would
      //    skip the PUT and its success toast, so surface the toast here to keep
      //    the original behavior (avatar-only saves still confirm).
      if (imageChanged && Object.keys(payload).length === 0) {
        message.success(formatSuccessToast(PLAYER_SUCCESS_MESSAGES.profileEdit))
        onSuccess?.()
        finishSection()
        return
      }

      // 3. Player PUT + error mapping delegated to the shared submit core.
      //    Web saves the whole form (no `fields` filter).
      await submitPlayer(values, {
        playerId,
        initialValues,
        update: ({ playerId: pid, payload: updatePayload }) =>
          updatePlayer.mutateAsync({ playerId: pid, payload: updatePayload }),
        setErrors: (errs) => helpers.setErrors(errs),
        setTouched: (touched) => helpers.setTouched(touched, false),
        titleMap: { ...BE_TO_FIELD_TITLE, ...ADMIN_BE_TO_FIELD_TITLE },
        // The extra sections ride along in the same PUT.
        extraPayload: toProfileExtrasPayload(values, initialValues, {
          audience: 'self',
        }),
        extraFieldMap: ADMIN_BE_TO_FORM_FIELD,
        notifySuccess: () =>
          message.success(
            formatSuccessToast(PLAYER_SUCCESS_MESSAGES.profileEdit)
          ),
        notifyError: (m) => message.error(m),
        // Bring the first errored field into view + focus it.
        onFieldErrors: () => scrollToFirstFieldError(),
        // The modal stays open: the section returns to read mode.
        onSuccess: () => {
          onSuccess?.()
          finishSection()
        },
      })
    } finally {
      helpers.setSubmitting(false)
    }
  }

  // Covers the cold fetch (skeleton) AND the full save flow — the image step and
  // the PUT — so Save/Cancel stay disabled across the whole sequential save and a
  // second click can't race an in-flight image upload before the PUT runs.
  const isSaving = isImagePending || updatePlayer.isPending
  const isLoading = isFetchLoading || isSaving

  const sectionEdit: SectionEditContextValue = {
    editingSection,
    isSaving,
    startEditing: (sectionKey) => setEditingSection(sectionKey),
    stopEditing: () => setEditingSection(null),
  }

  const content = (
    <EditPlayerContext.Provider
      value={{
        isLoading,
        isFetchLoading,
        playerName: formatPlayerName(data),
        onCancel: onClose,
        playerId,
        salesforceId: data?.sf_id,
      }}
    >
      {children}
    </EditPlayerContext.Provider>
  )

  return (
    <Formik<PlayerDetailsFormValues>
      key={formikKey}
      // Re-seed after a section save refetches the player. Deep-equal initial
      // values (a background refetch of unchanged data) leave edits alone.
      enableReinitialize
      initialValues={initialValues}
      validateOnChange={false}
      validateOnBlur={false}
      onSubmit={handleSubmit}
    >
      {/* Read mode needs a saved player; without one the form stays a plain,
          always-editable form. */}
      {playerId ? (
        <SectionEditContext.Provider value={sectionEdit}>
          {content}
        </SectionEditContext.Provider>
      ) : (
        content
      )}
    </Formik>
  )
}
